/**
 * Prefill Codec
 *
 * Encode and decode calculator prefill payloads for URL query params.
 * Uses JSON + URL encoding with version checking for forward compatibility.
 */

import { logger } from '@/lib/logger';
import type { CalculatorPrefill } from './types';

/**
 * Encode a prefill payload to a URL-safe string
 */
export function encodePrefill(prefill: CalculatorPrefill): string {
  try {
    const json = JSON.stringify(prefill);
    return encodeURIComponent(json);
  } catch {
    // Should never happen with valid prefill, but be safe
    return '';
  }
}

/**
 * Decode a prefill payload from a URL-safe string.
 * Returns null if decoding fails or the payload is invalid.
 */
export function decodePrefill(raw: string): CalculatorPrefill | null {
  if (!raw) {
    return null;
  }

  try {
    const json = decodeURIComponent(raw);
    const parsed = JSON.parse(json);

    // Validate version
    if (parsed.v !== 1) {
      logger.warn('[prefillCodec] Unknown prefill version:', parsed.v);
      return null;
    }

    // Validate required field: plate
    if (typeof parsed.plate !== 'string' || !parsed.plate) {
      logger.warn('[prefillCodec] Missing or invalid plate in prefill');
      return null;
    }

    // Return the parsed payload (trust the shape for v1)
    return parsed as CalculatorPrefill;
  } catch (error) {
    logger.warn('[prefillCodec] Failed to decode prefill:', error);
    return null;
  }
}

/**
 * Build a URL with prefill query parameter
 */
export function buildPrefillUrl(basePath: string, prefill: CalculatorPrefill): string {
  const encoded = encodePrefill(prefill);
  if (!encoded) {
    return basePath;
  }

  // Check if basePath already has query params
  const separator = basePath.includes('?') ? '&' : '?';
  return `${basePath}${separator}prefill=${encoded}`;
}
